import { useNavigate, useParams } from "react-router-dom";
import GoBack from "../components/GoBack";
import { useEffect, useState } from "react";
import useFlow from "../hooks/useFlow";
import { clearHubSpotState } from "../utils/hubspot";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import useStore from "../hooks/useStore";
import ClientCheckinCard from "../components/ClientCheckinCard";

export default function ClassEventCheckinList({ type }) {
  const {
    loadScheduleVisits,
    checkinSchedule,
    selectedSchedule,
    loadScheduleById,
    setResource,
    setShowMapResourceModal,
    setShowOfferingModal,
    quitSchedule,
    isDebugOn,
    setShowAskPhoneModal,
    setPendingOffering,
    setLastCheckinClient,
    locationPreferences,
  } = useFlow();
  const { setShowEventDetailModal } = useStore();
  const navigate = useNavigate();
  const { classId } = useParams();

  const [allCheckins, setAllCheckins] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [notCheckedIn, setNotCheckedIn] = useState([]);
  const [loading, setLoading] = useState({});
  const [loadingApi, setLoadingApi] = useState(false);

  useEffect(() => {
    if (!classId) return navigate(-1);
    if (!selectedSchedule || selectedSchedule?.id != classId)
      loadScheduleById(classId);
    else getCheckins();
  }, [classId, selectedSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortAndSetCheckins = (checkins) => {
    const sorted = checkins.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    setAllCheckins(sorted);

    const checkedIn = sorted.filter((checkin) => checkin.checkin);
    setCheckins(checkedIn);

    const notCheckedIn = sorted.filter((checkin) => !checkin.checkin);
    setNotCheckedIn(notCheckedIn);

    setLoadingApi(false);
  };

  const getCheckins = async () => {
    try {
      setLoadingApi(true);
      setCheckins([]);
      setNotCheckedIn([]);
      // One call, always fresh. This used to fire twice: a cached read to paint the
      // list immediately, then the real one to correct it, because the real one could
      // take 40s. The query behind it now takes ~2ms, so the cached pass only bought
      // a flicker and a roster that could be an hour stale.
      loadScheduleVisits(selectedSchedule.id)
        .then((locCheckins) => locCheckins && sortAndSetCheckins(locCheckins))
        .catch((e) => toast.error(`Something went wrong! ${e?.message}`));
    } catch (e) {
      toast.error(`Something went wrong! ${e?.message}`);
      setLoadingApi(false);
    }
  };

  const goJoin = () => {
    navigate(`./join`, { state: { allCheckins } });
  };

  const doCheckinOut = async (visitId, checkin, tags, clientId, havePhone) => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, [visitId]: true }));
      const res = await checkinSchedule(visitId, checkin, clientId);
      if (checkin) {
        setResource({
          name: selectedSchedule.resourceName || null,
          id: selectedSchedule.resourceId || null,
          tags: tags || null,
        });
        if (res?.offering && locationPreferences?.offeringEnabled !== false) {
          const visit = allCheckins.find((c) => c.clientId === clientId);
          const client = visit
            ? { id: visit.clientId, name: visit.name }
            : { id: clientId };
          setPendingOffering({ ...res.offering, _client: client });
          setLastCheckinClient(client);
        }
        if (!havePhone) setShowAskPhoneModal(true);
        else if (res?.offering && locationPreferences?.offeringEnabled !== false) {
          setShowOfferingModal(true);
        } else {
          setShowMapResourceModal(true);
        }
        clearHubSpotState();
      }
      await getCheckins();

      setLoading((prevLoading) => ({ ...prevLoading, [visitId]: false }));
    } catch (e) {
      setLoading((prevLoading) => ({ ...prevLoading, [visitId]: false }));
      toast.error(`Something went wrong! ${e?.message}`);
    }
  };

  const doQuit = async (clientId, visitId) => {
    try {
      await quitSchedule(classId, clientId, visitId);
      await getCheckins();
    } catch (e) {
      toast.error(`Something went wrong! ${e?.message}`);
    }
  };

  const eventDetailsHandler = () => {
    setShowEventDetailModal(true);
  };

  return (
    <>
      <GoBack />

      <div className="container-fluid d-block" id="app-class-details">
        <div className="primary-screen">
          <div className="row flex-nowrap sidebar">
            {selectedSchedule && (
              <div
                className="col-auto col-md-3 px-0 bg-white mx-auto d-none d-md-block min-vh-100"
                style={{ maxWidth: 500 }}
              >
                <div
                  className="align-items-center px-3 pt-2 min-vh-100 text-center h-100 position-fixed"
                  style={{ width: "inherit", maxWidth: "inherit" }}
                >
                  <div className="row text-center align-items-center h-100">
                    <div className="col-md-12 m-auto">
                      <div
                        className="ratio ratio-1x1 rounded-4"
                        style={{
                          background: `url(${
                            selectedSchedule.staffImage || "/placeholder.jpg"
                          }) no-repeat center`,
                          backgroundSize: "cover",
                        }}
                      ></div>
                      <div className="p-4" id="class_info">
                        <p className="p-0 m-0">
                          {type === "event" &&
                            selectedSchedule.startDateFormatted + ", "}
                          {selectedSchedule.startTimeFormatted}
                          {" - "}
                          {selectedSchedule.endTimeFormatted}
                        </p>
                        <h3 className="py-2 m-0 fw-bold">
                          {selectedSchedule.name}
                        </h3>
                        <p className="p-0 m-0 item-info">
                          <small>with</small> {selectedSchedule.staffName}{" "}
                          {selectedSchedule.resourceName && <small>in</small>}{" "}
                          {selectedSchedule.resourceName}
                        </p>
                      </div>
                      {type == "events" && (
                        <p>
                          <a className="pointer" data-testid="event-details-link" onClick={eventDetailsHandler}>
                            Read about the event &gt;
                          </a>
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary btn-lg mt-5"
                        data-testid="join-class-btn"
                        onClick={goJoin}
                      >
                        Join {type === "events" ? "event" : "class"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="col py-3 text-center">
              <div className="pt-5">
                <div className="container py-3 px-0">
                  <div className="row align-items-center">
                    <div className="col-12 col-md-10 col-xl-9 col-xxl-7 mx-auto class-list">
                      {notCheckedIn.map((checkin, idx) => (
                        <ClientCheckinCard
                          checkin={checkin}
                          doCheckinOut={doCheckinOut}
                          doQuit={doQuit}
                          loading={loading}
                          isDebugOn={isDebugOn}
                          key={idx}
                        />
                      ))}

                      {loadingApi && (
                        <div className="row py-2 d-flex student">
                          <div className="col-12">
                            <Skeleton
                              count={4}
                              height={100}
                              className="mb-3 rounded-4"
                            />
                          </div>
                        </div>
                      )}

                      <div className="row pb-5 pt-3 d-flex text-center">
                        <div className="col-12 text-center">
                          <p className="pt-5">
                            <b>Don&apos;t see your name?</b> <br />
                            <a
                              className="join-class-link mt-3 btn btn-secondary btn-lg"
                              data-testid="join-class-btn"
                              onClick={goJoin}
                            >
                              Click here to join &gt;
                            </a>
                          </p>
                        </div>
                      </div>
                      {checkins.length > 0 && (
                        <p className="text-center opacity-50">
                          -- already checked in --
                        </p>
                      )}

                      {checkins.map((checkin, idx) => (
                        <ClientCheckinCard
                          checkin={checkin}
                          doCheckinOut={doCheckinOut}
                          doQuit={doQuit}
                          loading={loading}
                          isDebugOn={isDebugOn}
                          key={idx}
                          alreadyCheckedIn
                        />
                      ))}

                      {allCheckins.length > 0 && (
                        <div className="row py-3 d-flex text-center">
                          <div className="col-12 pt-5 text-center">
                            <p>
                              <small>
                                Total signed up: {allCheckins.length}
                              </small>
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
